/**
 * Les 3 slots de tendance : cycle, rotation, potentiel d'Accroche — SPEC-MOTEUR §8 et §4.4,
 * SPEC-CONTENU §3.5.
 *
 * Deux arbitrages structurent ce fichier, tous deux déjà tranchés :
 *
 *  - **A18** — le cycle est piloté par les durées **par tendance** de `trends.json`
 *    (`duree_emergente_h`, `duree_pic_h`, `duree_saturee_h`) et par `mult_pic`. Les durées
 *    fixes 32/48/48 h de SPEC-MOTEUR §8 et le **pic dégressif 2,5 → 2,0** de §4.4 sont
 *    **abandonnés** : le pic vaut `mult_pic`, statique, tel que la tendance le déclare.
 *  - **A27** — un slot porte `themes: string[]` (et non un `tag` unique) : un sujet est « sur
 *    la tendance » si son `theme` y figure.
 *
 * Pureté (CLAUDE.md) : aucune I/O, aucune horloge système, aucun `Math.random`. Le contenu
 * arrive **en paramètre** (arbitrage A25) et la rotation passe par `draw(..., "trend_rotation",
 * n° du slot)`.
 */
import { DUREE_MORTE_H, TREND_MULT } from "../content/schema.js";
import { H_JOUR, H_WINDOW_BOOST } from "./clock.js";
import { draw, uniformInt } from "./rng.js";
import { SEUIL_EVERGREEN, } from "./types.js";
// ─────────────────────────────────────────────────────────────────────────────
// §4.4 Table fermée du potentiel d'Accroche (version A18)
// ─────────────────────────────────────────────────────────────────────────────
/** Sujet hors tendance — « froid » (§4.4, PLAN §4.4). */
export const POTENTIEL_ACCROCHE_FROID = 0.6;
/**
 * Sujet hors tendance mais **à potentiel evergreen** — « démarrage faible » (§4.4).
 * Éligibilité : `potentiel_evergreen >= SEUIL_EVERGREEN` (arbitrage **A26**).
 */
export const POTENTIEL_ACCROCHE_EVERGREEN = 0.8;
/** Sur tendance saturée avec `Qualité_eff < 1,0` : malus « me-too » (§4.4). */
export const POTENTIEL_ACCROCHE_SATUREE_ME_TOO = 0.7;
/** Seuil de Qualité_eff sous lequel le malus « me-too » s'applique (§4.4). */
export const SEUIL_ME_TOO = 1.0;
/** Ordre du cycle (PLAN §4.8) : émergente → pic → saturée → morte. */
export const CYCLE_TENDANCE = ["emergente", "pic", "saturee", "morte"];
/** Phase suivante du cycle. `morte` est terminale : on n'en sort que par rotation (§8). */
export function phaseSuivante(phase) {
    switch (phase) {
        case "emergente":
            return "pic";
        case "pic":
            return "saturee";
        case "saturee":
            return "morte";
        case "morte":
            return "morte";
    }
}
/**
 * Durée d'une phase, en heures **éveillées** — arbitrage **A18** : lue sur la tendance.
 * `morte` n'a pas de durée par tendance : c'est la constante de contenu `DUREE_MORTE_H`
 * (SPEC-CONTENU §3.5), le délai avant rotation du slot.
 *
 * Une tendance absente du contenu (contenu remplacé sous une run en cours) rend `0` : le slot
 * arrive immédiatement à échéance et sera rotationné.
 */
export function dureePhase(trend, phase) {
    if (!trend)
        return 0;
    switch (phase) {
        case "emergente":
            return trend.duree_emergente_h;
        case "pic":
            return trend.duree_pic_h;
        case "saturee":
            return trend.duree_saturee_h;
        case "morte":
            return DUREE_MORTE_H;
    }
}
/** La définition de contenu d'un slot, ou `undefined` si le slot est vide / inconnu. */
export function tendanceDuSlot(content, slot) {
    if (slot.trend_id === "")
        return undefined;
    return content.trends.find((t) => t.id === slot.trend_id);
}
/**
 * Multiplicateur d'Accroche d'une tendance dans une phase donnée (table §4.4, version A18) :
 * émergente 1,3 · **pic `mult_pic`** · saturée 0,9 (0,7 si `Qualité_eff < 1,0`) · morte 0,6.
 *
 * Les valeurs fixes viennent de `TREND_MULT` (SPEC-CONTENU §3.5) — une seule source de vérité.
 */
export function multiplicateurPhase(trend, phase, qualiteEff) {
    switch (phase) {
        case "emergente":
            return TREND_MULT.emergente;
        case "pic":
            // A18 : le pic dégressif 2,5 → 0,5 × avancement est abandonné au profit de `mult_pic`.
            return trend?.mult_pic ?? TREND_MULT.emergente;
        case "saturee":
            return qualiteEff !== undefined && qualiteEff < SEUIL_ME_TOO
                ? POTENTIEL_ACCROCHE_SATUREE_ME_TOO
                : TREND_MULT.saturee;
        case "morte":
            return TREND_MULT.morte;
    }
}
// ─────────────────────────────────────────────────────────────────────────────
// « Ce sujet est-il sur une tendance ? » (A27)
// ─────────────────────────────────────────────────────────────────────────────
/** Un slot occupé porte-t-il ce thème ? (arbitrage **A27**) */
export function slotPorteLeTheme(slot, theme) {
    return slot.trend_id !== "" && slot.themes.includes(theme);
}
/**
 * Potentiel d'Accroche d'un sujet (table §4.4) et situation de tendance associée.
 *
 * Ce que cette fonction ne fait **pas**, et qui appartient à l'appelant (M1.9) :
 *  - le modificateur d'alignement (+0,3 chaos / −0,1 lumière) — appliqué par
 *    `computeVideoOutcome` (`formula.ts`) ;
 *  - les `potential_add` d'Accroche du contenu — `collectModifiers(...).potential.accroche`
 *    s'**ajoute** à cette table (cf. `effects.ts`, arbitrage A21).
 *
 * `qualite_eff` n'est lu que pour le malus « me-too » de la phase saturée. Si plusieurs slots
 * portent le thème du sujet, c'est le **plus favorable** qui gagne (potentiel le plus élevé,
 * puis plus petit index de slot) ✎ — la spec ne tranche pas le cas, et servir le meilleur est
 * la lecture la plus lisible pour le joueur (« ton sujet est sur la tendance en pic »).
 */
export function situationTendance(state, sujet, content, qualiteEff) {
    let meilleur = null;
    state.trends.forEach((slot, index) => {
        if (!slotPorteLeTheme(slot, sujet.theme))
            return;
        const trend = tendanceDuSlot(content, slot);
        const potentiel = multiplicateurPhase(trend, slot.phase, qualiteEff);
        const candidat = {
            slot: index,
            trend_id: slot.trend_id,
            phase: slot.phase,
            potentiel,
            chaude: slot.phase === "emergente" || slot.phase === "pic",
        };
        if (meilleur === null || candidat.potentiel > meilleur.potentiel)
            meilleur = candidat;
    });
    if (meilleur !== null)
        return meilleur;
    // Hors tendance : froid, ou « démarrage faible » si le sujet est éligible evergreen (A26).
    return {
        slot: null,
        trend_id: null,
        phase: null,
        potentiel: sujet.potentiel_evergreen >= SEUIL_EVERGREEN
            ? POTENTIEL_ACCROCHE_EVERGREEN
            : POTENTIEL_ACCROCHE_FROID,
        chaude: false,
    };
}
/** Raccourci : le potentiel d'Accroche seul (table §4.4). */
export function potentielAccroche(state, sujet, content, qualiteEff) {
    return situationTendance(state, sujet, content, qualiteEff).potentiel;
}
/**
 * Le sujet est-il sur une tendance **chaude** (émergente ou pic) ? C'est la condition 2 de la
 * classification §6.1 (classe `trend`).
 */
export function estSurTendanceChaude(state, theme) {
    return state.trends.some((slot) => slotPorteLeTheme(slot, theme) && (slot.phase === "emergente" || slot.phase === "pic"));
}
/**
 * ✎ Préavis d'une vague qui retombe : **un jour simulé**. En dessous, l'UI alerte — au-dessus,
 * la barre d'avancement suffit. Le même repère que `SEUIL_BIENTOT_PERIMEE_H` pour les idées :
 * deux pressions différentes, un seul langage de préavis, donc rien de nouveau à apprendre.
 */
export const H_ALERTE_VAGUE = H_JOUR; // ✎ un jour simulé
/** La lecture complète d'un slot — tout ce dont l'UI a besoin, en une passe. */
export function lectureTendance(state, content, slotIndex) {
    const slot = state.trends[slotIndex];
    if (!slot || slot.trend_id === "")
        return null;
    const trend = tendanceDuSlot(content, slot);
    const duree = dureePhase(trend, slot.phase);
    const restantes = duree > 0 ? Math.max(0, duree - slot.phase_elapsed_h) : null;
    const suivante = phaseSuivante(slot.phase);
    const multCourant = multiplicateurPhase(trend, slot.phase);
    const multPic = trend?.mult_pic ?? TREND_MULT.emergente;
    return {
        slot: slotIndex,
        trend_id: slot.trend_id,
        nom_connu: trend !== undefined,
        phase: slot.phase,
        themes: [...slot.themes],
        alignment: slot.alignment,
        heures_restantes: restantes,
        jours_restants: restantes === null ? null : Math.floor(restantes / H_JOUR),
        avancement: duree > 0 ? Math.min(1, slot.phase_elapsed_h / duree) : 1,
        mult_courant: multCourant,
        // `morte` est terminale : la « suivante » reste `morte`, et le multiplicateur avec.
        mult_suivant: multiplicateurPhase(trend, suivante),
        mult_pic: multPic,
        mult_manque: Math.max(0, multPic - multCourant),
        chaude: slot.phase === "emergente" || slot.phase === "pic",
        bientot_finie: slot.phase !== "morte" && restantes !== null && restantes <= H_ALERTE_VAGUE,
    };
}
/**
 * ⚠️ **Interaction signalée entre ⟦A-15 §2⟧ et le contenu existant.** `content/skills.json`
 * porte déjà une capacité `phase_tendance_visible` (nœud *Analytics*, PLAN §4.8) dont la
 * définition est mot pour mot « révèle phase + durée restante des tendances ». Or A-15 §2
 * demande de rendre la vague visible **pour que la pression existe**.
 *
 * Les deux ne se contredisent pas si l'on sépare ce que le MOTEUR sait de ce que l'UI MONTRE :
 * `lectureTendance` rend toujours la vérité (le serveur en a besoin, les tests aussi), et
 * cette fonction dit à l'UI si le joueur a le droit de la lire en clair. Sans le nœud,
 * l'écran affiche la phase **sans le chiffre** — on sent que la vague retombe, on ne sait pas
 * exactement quand. C'est précisément ce qu'*Analytics* est censé acheter.
 *
 * **À trancher par Mathieu** : si la pression d'A-15 §2 doit exister dès l'arc 1, il faut soit
 * ouvrir le préavis à tous (et *Analytics* n'achète plus que la précision), soit accepter que
 * la vague reste floue jusqu'à l'achat du nœud. Le moteur laisse les deux ouvertes.
 */
export const CAPACITE_PHASE_TENDANCE = "phase_tendance_visible";
/** Les trois slots lus d'un coup — les slots vides sont omis. */
export function tendancesLisibles(state, content) {
    return state.trends
        .map((_, i) => lectureTendance(state, content, i))
        .filter((l) => l !== null);
}
/**
 * **Ce qu'on laisse filer sur un THÈME donné** — la lecture que l'écran d'inspiration pose sur
 * chaque carte : « ton idée est sur *Le drama du moment*, en pic, plus que 12 h ».
 *
 * Rend `null` si aucun slot actif ne porte le thème : hors tendance, il n'y a **rien à
 * rater**, et afficher un compte à rebours vide serait précisément le jeu anxiogène qu'A-15
 * interdit.
 */
export function vagueDuTheme(state, content, theme) {
    let meilleure = null;
    state.trends.forEach((slot, i) => {
        if (!slotPorteLeTheme(slot, theme))
            return;
        const lecture = lectureTendance(state, content, i);
        if (!lecture)
            return;
        // Même règle d'arbitrage que `situationTendance` : on sert le slot le plus favorable.
        if (meilleure === null || lecture.mult_courant > meilleure.mult_courant)
            meilleure = lecture;
    });
    return meilleure;
}
/**
 * Les vagues **chaudes qu'on est en train de rater** : exploitables maintenant, mais dont
 * aucune idée en main ne porte le thème. C'est littéralement la question d'A-15 — *« la vague
 * qu'on n'a pas prise »* — et la réponse est une invitation à faire une veille, pas une menace.
 */
export function vaguesRatees(state, content) {
    const themesEnMain = new Set(state.inspiration
        .map((c) => content.subjects.find((s) => s.id === c.subject_id)?.theme)
        .filter((t) => t !== undefined));
    return tendancesLisibles(state, content).filter((l) => l.chaude && !l.themes.some((t) => themesEnMain.has(t)));
}
/**
 * Remplit un slot avec une tendance tirée uniformément dans `content.trends`, **en excluant
 * les tendances déjà actives** (§8) — y compris celle qui meurt, pour qu'un slot ne se
 * re-tire jamais la tendance qu'il vient d'épuiser ✎ (c'est aussi ce que fait `bootstrapRun`).
 *
 * Un seul `draw`, purpose `trend_rotation`, **index = n° du slot** (§8). MUTE `state`.
 * Rend `null` si aucun candidat n'est disponible : le slot reste en place plutôt que de se
 * vider (une run avec moins de 4 tendances de contenu ne doit pas perdre ses slots).
 */
export function rotationSlot(state, content, seed, slotIndex) {
    const dejaActives = state.trends.map((t) => t.trend_id).filter((id) => id !== "");
    const candidats = content.trends.filter((t) => !dejaActives.includes(t.id));
    if (candidats.length === 0)
        return null;
    const u = draw(seed, state.turn, "trend_rotation", slotIndex);
    const choisie = candidats[uniformInt(u, 0, candidats.length - 1)];
    state.trends[slotIndex] = {
        trend_id: choisie.id,
        themes: [...choisie.themes],
        alignment: choisie.alignement,
        phase: "emergente",
        // Une tendance qui naît naît maintenant : le reliquat d'heures de l'ancienne n'est pas
        // reporté ✎ (et cela garantit au plus une rotation par slot et par résolution, donc pas
        // deux tirages sur le même `(seed, turn, purpose, index)`).
        phase_elapsed_h: 0,
    };
    return choisie;
}
/**
 * §2.4 pas 5 — `phase_elapsed_h += Δh`, phases échues avancées, slots morts rotationnés,
 * expiration de `window_boost`.
 *
 * MUTE `state` : appelée depuis la résolution de nuit, qui travaille déjà sur le clone de la
 * file §2.3. Le reliquat d'heures est **reporté d'une phase à la suivante** (une nuit qui suit
 * une `pause` de 112 h peut traverser plusieurs phases), mais pas au travers d'une rotation.
 */
export function avancerTendances(state, content, seed, deltaH, events = []) {
    const changements = [];
    // ⟦A-15 §2⟧ Photo d'AVANT, pour n'alerter qu'au **franchissement** du seuil de préavis.
    // Mesuré sans elle : 757 alertes « la vague retombe » sur une run de 90 jours — le préavis
    // devenait du bruit, et un préavis qu'on n'écoute plus ne prévient plus rien.
    const avant = state.trends.map((slot, index) => ({
        phase: slot.phase,
        restantes: lectureTendance(state, content, index)?.heures_restantes ?? null,
    }));
    state.trends.forEach((slot, index) => {
        if (slot.trend_id === "")
            return;
        slot.phase_elapsed_h += Math.max(0, deltaH);
        // Phases vivantes échues : on avance en reportant le reliquat.
        let garde = 0;
        while (slot.phase !== "morte") {
            const duree = dureePhase(tendanceDuSlot(content, slot), slot.phase);
            if (duree > 0 && slot.phase_elapsed_h < duree)
                break;
            slot.phase_elapsed_h = duree > 0 ? slot.phase_elapsed_h - duree : 0;
            slot.phase = phaseSuivante(slot.phase);
            changements.push({
                slot: index,
                raison: "phase",
                trend_id: slot.trend_id,
                phase: slot.phase,
            });
            if (++garde > CYCLE_TENDANCE.length)
                break; // filet : durées de contenu à 0
        }
        // Morte depuis assez longtemps : rotation du slot (§8).
        if (slot.phase === "morte" && slot.phase_elapsed_h >= DUREE_MORTE_H) {
            const mourante = slot.trend_id;
            const nouvelle = rotationSlot(state, content, seed, index);
            if (nouvelle) {
                changements.push({
                    slot: index,
                    raison: "rotation",
                    trend_id: nouvelle.id,
                    phase: "emergente",
                    remplace: mourante,
                });
            }
        }
    });
    for (const c of changements) {
        events.push({
            kind: "trend_changed",
            data: {
                slot: c.slot,
                raison: c.raison,
                trend_id: c.trend_id,
                phase: c.phase,
                remplace: c.remplace ?? null,
            },
        });
    }
    // ⟦A-15 §2⟧ **Le préavis, et c'est lui qui fait toute la différence.** Signaler qu'une
    // tendance vient de mourir ne sert à rien — c'est déjà trop tard. Ce que le joueur doit
    // recevoir, c'est « la vague *X* retombe demain », pendant qu'il peut encore décider.
    // Émis au sommeil, donc au moment précis où il planifie sa journée.
    state.trends.forEach((slot, index) => {
        if (slot.trend_id === "")
            return;
        const lecture = lectureTendance(state, content, index);
        if (!lecture || !lecture.chaude || !lecture.bientot_finie)
            return;
        // Un seul préavis par vague : on n'alerte qu'au moment où elle ENTRE dans la dernière
        // ligne droite. Un changement de phase compte comme une entrée — c'est une autre vague.
        const photo = avant[index];
        const etaitDejaDansLaBande = photo.phase === slot.phase && photo.restantes !== null && photo.restantes <= H_ALERTE_VAGUE;
        if (etaitDejaDansLaBande)
            return;
        events.push({
            kind: "warning",
            data: {
                scripted: "vague_retombe",
                slot: index,
                trend_id: lecture.trend_id,
                phase: lecture.phase,
                heures_restantes: lecture.heures_restantes,
                // Ce qu'on perd à ne pas la prendre : le multiplicateur d'Accroche qu'on laisse filer.
                mult_courant: lecture.mult_courant,
                mult_suivant: lecture.mult_suivant,
                themes: lecture.themes.join(","),
            },
        });
    });
    return changements;
}
// ─────────────────────────────────────────────────────────────────────────────
// Fenêtre d'opportunité (×3 pendant 24 h simulées)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦C5⟧ **Le flag de contenu qui ouvre la fenêtre.** SPEC-MOTEUR §8 dit que les fenêtres
 * d'opportunité *« sont des événements de contenu qui posent
 * `flags.window_boost_expires_h = clock_h + 24` »* — mais **aucun type d'effet ne pouvait
 * poser ce champ** : `ouvrirFenetreOpportunite` n'avait aucun appelant, et la condition de
 * `publish.ts` était donc toujours fausse. La mécanique était entièrement morte.
 *
 * Le chemin de données qui existait déjà, sans toucher au schéma : `flag_add` porte un
 * `expires_h` optionnel depuis toujours. Un événement de contenu qui écrit
 * `{"type":"flag_add","flag":"window_boost","expires_h":24}` ouvre donc la fenêtre, et elle se
 * referme toute seule à l'échéance — sans que le moteur ait à la purger.
 *
 * ⚠️ **Entorse assumée à « contenu = données »**, la même qu'⟦A35⟧ : le moteur lit un id de
 * contenu. L'alternative — un 20e type d'effet dans l'enum fermé du §2.3 — serait une
 * modification de SPEC-CONTENU, hors périmètre d'une session de moteur, et personne ne
 * l'utiliserait tant que le contenu n'aurait pas été réécrit. Le nom est ici, en constante
 * exportée, pour que le contenu et la spec parlent du même mot.
 */
export const FLAG_FENETRE_OPPORTUNITE = "window_boost";
/**
 * Une fenêtre d'opportunité est-elle ouverte ? (§4.5, arbitrage **A22** : le ×3 s'applique à
 * VuesPic, pas au potentiel d'Accroche.)
 *
 * Deux sources, et c'est voulu : le **champ d'état** (posé par `ouvrirFenetreOpportunite`,
 * la route systémique — télécommande de dev, scénarios imposés) et le **flag de contenu**
 * (la route de données, voir `FLAG_FENETRE_OPPORTUNITE`).
 */
export function windowBoostActif(state, flagDeContenu = false) {
    const fin = state.flags.window_boost_expires_h;
    return flagDeContenu || (fin !== null && state.clock_h < fin);
}
/**
 * Ouvre une fenêtre d'opportunité de `H_WINDOW_BOOST` heures simulées (§8 : « les fenêtres
 * d'opportunité sont des événements de contenu qui posent
 * `flags.window_boost_expires_h = clock_h + 24` »). MUTE `state` ; rend l'échéance posée.
 */
export function ouvrirFenetreOpportunite(state) {
    state.flags.window_boost_expires_h = state.clock_h + H_WINDOW_BOOST;
    return state.flags.window_boost_expires_h;
}
/**
 * Expiration de la fenêtre au sommeil (§2.4 pas 5). MUTE `state` ; rend `true` si une fenêtre
 * vient d'être refermée.
 */
export function expirerFenetreOpportunite(state) {
    const fin = state.flags.window_boost_expires_h;
    if (fin !== null && state.clock_h >= fin) {
        state.flags.window_boost_expires_h = null;
        return true;
    }
    return false;
}
//# sourceMappingURL=trends.js.map